import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';

import { GableSettings, GableStuddingDirection } from '@paragon/contract-library';
import { DistanceField } from '@paragon/material';
import { KeyToValueOfType } from '@paragon/utilities';

import { updateActiveGableSettings } from '../../../../store/actions/editorActions';
import { getComponentDesignInsertionInfoOrNull } from '../../../../store/selectors/insertionObjectSelectors';
import { getPickingObjectInfo } from '../../../../store/selectors/pickingSelectors';
import { applicationOnPickingProfileJoint } from '../../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { ActiveComponentContinuouslyBearingSettings } from './ContinuouslyBearingSettings';

interface Props {
  readonly disabled?: boolean;
  readonly hideGableCheckbox?: boolean;
  readonly showContinuouslyBearing?: boolean;
  readonly value: GableSettings;
  readonly pickingProfileJoint?: boolean;
  readonly onChange: (newValue: GableSettings) => void;
  readonly selectProfileJoint?: () => void;
}

export const GableSettingsSubPanel = (props: Props) => {
  const updateSetting = <K extends keyof GableSettings>(key: K, value: GableSettings[K]) => {
    props.onChange({
      ...props.value,
      [key]: value,
    });
  };

  const checkedChange = (name: keyof GableSettings) => (event: React.ChangeEvent<HTMLInputElement>) => {
    updateSetting(name, event.target.checked);
  };

  const onChangeDistance = (key: KeyToValueOfType<GableSettings, number>) => (value: number) =>
    updateSetting(key, value);

  const directionChange = (event: SelectChangeEvent<GableStuddingDirection>) => {
    const newDirection = event.target.value as GableSettings['studdingDirection'];
    updateSetting('studdingDirection', newDirection);
    if (newDirection === 'LocationOutward' && props.selectProfileJoint) {
      props.selectProfileJoint();
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
        {props.hideGableCheckbox ? null : (
          <FormControlLabel
            control={<Checkbox checked={props.value?.isGable} onChange={checkedChange('isGable')} />}
            label="Gable"
            disabled={props.disabled}
          />
        )}

        <FormControlLabel
          control={<Checkbox checked={props.value.isStructuralGable} onChange={checkedChange('isStructuralGable')} />}
          label="Structural Gable"
          disabled={!props.value?.isGable}
        />
        {props.showContinuouslyBearing ? <ActiveComponentContinuouslyBearingSettings /> : null}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        <DistanceField
          label="Stud Spacing"
          value={props.value.studSpacing}
          min={0}
          onSubmit={onChangeDistance('studSpacing')}
          disabled={!props.value?.isGable}
          sx={{ flexBasis: '50%' }}
        />

        <FormControl size="small" variant="outlined" sx={{ flexBasis: '50%', marginTop: 2 }}>
          <InputLabel htmlFor="studding-direction">Studding Direction</InputLabel>
          <Select
            value={props.value.studdingDirection}
            onChange={directionChange}
            inputProps={{ id: 'studding-direction' }}
            disabled={!props.value?.isGable}
            label="Studding Direction"
          >
            <MenuItem value="CenterOutward">Center Outward</MenuItem>
            <MenuItem value="LeftToRight">Left to Right</MenuItem>
            <MenuItem value="RightToLeft">Right to Left</MenuItem>
            <MenuItem value="LocationOutward">Location Outward</MenuItem>
            <MenuItem value="EndsInward">Ends Inward</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {props.value.studdingDirection === 'LocationOutward' && props.value?.isGable && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between' }}>
          {props.showContinuouslyBearing
            ? null
            : props.selectProfileJoint && (
                <Button
                  sx={{ m: 1 }}
                  variant="outlined"
                  size="small"
                  disabled={props.disabled || props.pickingProfileJoint}
                  onClick={props.selectProfileJoint}
                >
                  {props.disabled || props.pickingProfileJoint ? 'Selecting Joint...' : 'Select Joint'}
                </Button>
              )}

          <DistanceField
            helperText={props.showContinuouslyBearing ? '' : 'Select a joint or enter start location manually'}
            label="Start Location"
            value={props.value.studStartLocation}
            onSubmit={onChangeDistance('studStartLocation')}
            disabled={props.disabled}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex' }}>
        <DistanceField
          label="Minimum Stud Length"
          value={props.value.minimumStudLength}
          min={0}
          onSubmit={onChangeDistance('minimumStudLength')}
          disabled={!props.value?.isGable}
        />
      </Box>
    </Box>
  );
};

export const ActiveComponentGableSettings = (props: Pick<Props, 'hideGableCheckbox' | 'showContinuouslyBearing'>) => {
  const dispatch = useAppDispatch();
  const gableSettings = useAppSelector(getComponentDesignInsertionInfoOrNull)?.designSettings.gableSettings;
  const pickingProfileJoint = useAppSelector((state) => getPickingObjectInfo(state)?.type === 'pickingProfileJoint');

  return (
    <GableSettingsSubPanel
      hideGableCheckbox={props.hideGableCheckbox}
      showContinuouslyBearing={props.showContinuouslyBearing}
      disabled={!gableSettings}
      value={gableSettings!}
      pickingProfileJoint={pickingProfileJoint}
      onChange={(newValue) => dispatch(updateActiveGableSettings(newValue))}
      selectProfileJoint={() => dispatch(applicationOnPickingProfileJoint())}
    />
  );
};
